import { Searchable, type SearchResult } from '@domains/subscription/priv/components/Searchable'
import { CreateRelationFormFactory } from '@domains/subscription/priv/Relations'
import { RelationsListFactory } from '@domains/subscription/priv/Relations/factories/RelationsListFactory'
import { DefaultConfigForm } from '@domains/subscription/priv/Templates/DefaultConfigForm'
import { DefaultConfigRow } from '@domains/subscription/priv/Templates/DefaultConfigRow'
import { NoSearchResults } from '@domains/subscription/priv/Templates/NoSearchResults'
import { TemplateRow } from '@domains/subscription/priv/Templates/TemplateRow'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Button, Dialog, List, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useState } from 'react'
import { Content } from '../components/Content'
import type { CreateDefaultConfigFormData, CreateTemplateFormData, DefaultConfig, PricePlanTemplate } from '../types'
import { type CreateTemplateOptions, TemplateForm } from './TemplateForm'

enum TemplateTabs {
  SUBSCRIPTION_PLANS = 'SUBSCRIPTION_PLANS',
  DEFAULT_CONFIGS = 'DEFAULT_CONFIGS',
  BULK_MIGRATION = 'BULK_MIGRATION',
  RELATIONS = 'RELATIONS',
}

const tabRouteSuffixByTab: Record<TemplateTabs, string> = {
  [TemplateTabs.SUBSCRIPTION_PLANS]: '',
  [TemplateTabs.RELATIONS]: 'relations',
  [TemplateTabs.DEFAULT_CONFIGS]: 'default-configs',
  [TemplateTabs.BULK_MIGRATION]: 'bulk-migration',
}

const tabByRouteSuffix: Record<string, TemplateTabs> = {
  relations: TemplateTabs.RELATIONS,
  'default-configs': TemplateTabs.DEFAULT_CONFIGS,
  'bulk-migration': TemplateTabs.BULK_MIGRATION,
}

const getTabFromPathname = (pathname: string): TemplateTabs => {
  const subscriptionPathMatch = pathname.match(/^\/subscription(?:\/([^/]+))?/)
  const routeSuffix = subscriptionPathMatch?.[1]

  if (!routeSuffix) {
    return TemplateTabs.SUBSCRIPTION_PLANS
  }

  return tabByRouteSuffix[routeSuffix] ?? TemplateTabs.SUBSCRIPTION_PLANS
}

export type TemplatesProps = {
  templates: PricePlanTemplate[]
  options: CreateTemplateOptions
  defaultConfigs: DefaultConfig[]
  onNavigateToTemplate: (templateId: string) => void
  onNavigateToDefaultConfig: (configId: string) => void
  onCreateTemplate: (formData: CreateTemplateFormData) => Promise<void>
  onSaveDefaultConfig: (formData: CreateDefaultConfigFormData) => void
  isPatching: boolean
  supportedCurrencies: string[]
  supportedPartners: string[]
  bulkMigrationSlot?: React.ReactNode
  onNavigate: (path: string) => void
  pathname: string
  search: [URLSearchParams, SetSearchParams]
}

export type SetSearchParams = (params: URLSearchParams, options?: { replace?: boolean }) => void

export const Templates = ({
  templates,
  options,
  defaultConfigs,
  onNavigateToTemplate,
  onNavigateToDefaultConfig,
  onCreateTemplate,
  onSaveDefaultConfig,
  isPatching,
  supportedCurrencies,
  supportedPartners,
  bulkMigrationSlot,
  onNavigate,
  pathname,
  search,
}: TemplatesProps) => {
  const tab = getTabFromPathname(pathname)

  const [showForm, setShowForm] = useState(false)
  const [showDefaultConfigForm, setShowDefaultConfigForm] = useState(false)
  const [showCreateRelationTabForm, setShowCreateRelationTabForm] = useState(false)

  const [searchParams, setSearchParams] = search

  const handleSubmit = (data: CreateTemplateFormData) => {
    onCreateTemplate(data).then(() => {
      setShowForm(false)
    })
  }

  const handleSubmitDefaultConfig = (data: CreateDefaultConfigFormData): void => {
    onSaveDefaultConfig(data)
    setShowDefaultConfigForm(false)
  }

  const handleCreateClick = (): void => {
    switch (tab) {
      case TemplateTabs.SUBSCRIPTION_PLANS:
        setShowForm(true)
        break
      case TemplateTabs.DEFAULT_CONFIGS:
        setShowDefaultConfigForm(true)
        break
      case TemplateTabs.RELATIONS:
        console.log('set showCreateRelationTabForm(true)')
        setShowCreateRelationTabForm(true)
        break
      default:
        break
    }
  }

  const handleTabChange = (_: React.SyntheticEvent, nextTab: TemplateTabs): void => {
    const routeSuffix = tabRouteSuffixByTab[nextTab]
    const tabRoute = routeSuffix ? `/subscription/${routeSuffix}` : '/subscription'
    onNavigate(tabRoute)
  }

  const createButtonLabel = getActionButtonLabel(tab)

  const sortedTemplates = templates.toSorted((a, b) => a.name.localeCompare(b.name))

  const [searchedTemplates, setSearchedTemplates] = useState<SearchResult<PricePlanTemplate>>({
    query: '',
    results: sortedTemplates,
    isEmpty: sortedTemplates.length === 0,
  })

  const handleSearch = (data: SearchResult<PricePlanTemplate>) => {
    setSearchedTemplates(data)

    if (data.query === '') {
      searchParams.delete('templates')
    } else {
      searchParams.set('templates', data.query)
    }

    setSearchParams(searchParams, { replace: true })
  }

  return (
    <Stack
      sx={{
        flex: 1,
        paddingBottom: 2,
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h1">Subscriptions</Typography>
        {tab === TemplateTabs.BULK_MIGRATION ? (
          bulkMigrationSlot
        ) : (
          <Button variant="contained" color="primary" type="button" onClick={handleCreateClick}>
            {createButtonLabel}
          </Button>
        )}
      </Stack>
      <TabContext value={tab}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="Subscription templates" value={TemplateTabs.SUBSCRIPTION_PLANS} />
          <Tab label="Relations" value={TemplateTabs.RELATIONS} />
          <Tab label="Default configs" value={TemplateTabs.DEFAULT_CONFIGS} />
          <Tab label="Bulk migration" value={TemplateTabs.BULK_MIGRATION} />
        </Tabs>
        <TabPanel
          sx={{ padding: 0, paddingTop: 3, display: 'flex', overflow: 'hidden', minHeight: 0 }}
          value={TemplateTabs.SUBSCRIPTION_PLANS}
        >
          <Stack
            sx={{
              overflow: 'hidden',
              flex: 1,
              minHeight: 0,
              gap: 2,
            }}
          >
            <Searchable
              value={searchParams.get('templates') ?? ''}
              onSearch={handleSearch}
              data={sortedTemplates}
              label="Search templates"
              searchable={(item) => [item.name, item.externalName, item.id]}
            />
            <Content sx={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <List sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
                {searchedTemplates.isEmpty && <NoSearchResults query={searchedTemplates.query} />}
                {searchedTemplates.results.map((template) => (
                  <TemplateRow
                    key={template.id}
                    templateName={template.name}
                    templateExternalName={template.externalName}
                    customerCount={template.numberOfConnections}
                    onClick={() => onNavigateToTemplate(template.id)}
                  />
                ))}
              </List>
            </Content>
          </Stack>
        </TabPanel>
        <TabPanel
          sx={{ padding: 0, paddingTop: 3, display: 'flex', overflow: 'hidden', minHeight: 0 }}
          value={TemplateTabs.DEFAULT_CONFIGS}
        >
          <Content sx={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <List sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
              {defaultConfigs.map((config) => (
                <DefaultConfigRow
                  key={config.id}
                  channel={config.channel}
                  currencySupport={config.currencySupport}
                  onClick={() => onNavigateToDefaultConfig(config.id)}
                />
              ))}
            </List>
          </Content>
        </TabPanel>
        <TabPanel sx={{ padding: 0, paddingTop: 3, display: 'flex', overflow: 'hidden', minHeight: 0 }} value={TemplateTabs.RELATIONS}>
          <Content sx={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <List sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
              <RelationsListFactory />
            </List>
          </Content>
        </TabPanel>
        <TabPanel sx={{ padding: 0, paddingTop: 3 }} value={TemplateTabs.BULK_MIGRATION} />
      </TabContext>
      <Dialog role="dialog" open={showForm} onClose={() => setShowForm(false)} maxWidth="xs" fullWidth>
        <Box
          sx={{
            padding: 3,
          }}
        >
          <TemplateForm isPatching={isPatching} options={options} onSubmit={handleSubmit} />
        </Box>
      </Dialog>
      <Dialog role="dialog" open={showCreateRelationTabForm} onClose={() => setShowCreateRelationTabForm(false)} maxWidth="sm" fullWidth>
        <Box
          sx={{
            padding: 3,
          }}
        >
          <CreateRelationFormFactory onClose={() => setShowCreateRelationTabForm(false)} />
        </Box>
      </Dialog>
      <Dialog role="dialog" open={showDefaultConfigForm} onClose={() => setShowDefaultConfigForm(false)} maxWidth="xs" fullWidth>
        <Box
          sx={{
            padding: 3,
          }}
        >
          <DefaultConfigForm
            templates={templates}
            supportedCurrencies={supportedCurrencies}
            supportedPartners={supportedPartners}
            onSubmit={handleSubmitDefaultConfig}
          />
        </Box>
      </Dialog>
    </Stack>
  )
}

const getActionButtonLabel = (tab: TemplateTabs): string => {
  switch (tab) {
    case TemplateTabs.SUBSCRIPTION_PLANS:
      return 'Create Template'
    case TemplateTabs.DEFAULT_CONFIGS:
      return 'Create default config'
    case TemplateTabs.RELATIONS:
      return 'Create relation'
    default:
      return ''
  }
}
